import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase";

export interface HeroData {
  id: number;
  page: string;
  tagline: string | null;
  title: string;
  subtitle: string | null;
  description: string | null;
  cta_text: string | null;
  cta_link: string | null;
  background_image: string;
  overlay_gradient: string;
  nav_label: string | null;
  seo_title: string | null;
}

export function useHero(page: string, fallback?: HeroData) {
  const [data, setData] = useState<HeroData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  const fetchData = useCallback(async () => {
    if (!mounted.current) return;
    setLoading(true);
    setError(null);
    try {
      const { data: result, error: err } = await supabase
        .from("heroes")
        .select("*")
        .eq("page", page)
        .maybeSingle();
      if (err) throw err;
      if (mounted.current) {
        setData((result as HeroData | null) ?? null);
      }
    } catch {
      if (mounted.current) {
        setError("Error cargando hero");
        setData(null);
      }
    } finally {
      if (mounted.current) {
        setLoading(false);
      }
    }
  }, [page]);

  useEffect(() => {
    mounted.current = true;
    fetchData();
    return () => {
      mounted.current = false;
    };
  }, [fetchData]);

  // Devolver fallback solo si no hay datos de la BD y hay fallback disponible
  const finalData = data ?? fallback ?? null;

  return { data: finalData, loading, error, refetch: fetchData };
}